type CurriculumRelease = {
  id: string
  manifest: Record<string, unknown>
  manifest_hash: string
  revision: number
  published_at: string
}

type LearningEvent = {
  tenantId: string
  activityId: string
  attemptId: string
  level: number
  levelCompletion: number
  isCorrect: boolean
  timeTakenSeconds: number
}

const databaseName = 'wlearn-curriculum'
const releaseStore = 'releases'
const eventStore = 'learning-events'
let activeEventSync: Promise<void> | null = null

const openDatabase = () =>
  new Promise<IDBDatabase>((resolve, reject) => {
    const request = window.indexedDB.open(databaseName, 1)
    request.onerror = () => reject(request.error)
    request.onupgradeneeded = () => {
      const database = request.result
      if (!database.objectStoreNames.contains(releaseStore))
        database.createObjectStore(releaseStore)
      if (!database.objectStoreNames.contains(eventStore))
        database.createObjectStore(eventStore, { keyPath: 'attemptId' })
    }
    request.onsuccess = () => resolve(request.result)
  })

const read = <T>(database: IDBDatabase, storeName: string, key: IDBValidKey) =>
  new Promise<T | undefined>((resolve, reject) => {
    const request = database.transaction(storeName, 'readonly').objectStore(storeName).get(key)
    request.onerror = () => reject(request.error)
    request.onsuccess = () => resolve(request.result as T | undefined)
  })

const write = (database: IDBDatabase, storeName: string, value: unknown, key?: IDBValidKey) =>
  new Promise<void>((resolve, reject) => {
    const request = key === undefined
      ? database.transaction(storeName, 'readwrite').objectStore(storeName).put(value)
      : database.transaction(storeName, 'readwrite').objectStore(storeName).put(value, key)
    request.onerror = () => reject(request.error)
    request.onsuccess = () => resolve()
  })

const all = <T>(database: IDBDatabase, storeName: string) =>
  new Promise<T[]>((resolve, reject) => {
    const request = database.transaction(storeName, 'readonly').objectStore(storeName).getAll()
    request.onerror = () => reject(request.error)
    request.onsuccess = () => resolve(request.result as T[])
  })

const remove = (database: IDBDatabase, storeName: string, key: IDBValidKey) =>
  new Promise<void>((resolve, reject) => {
    const request = database.transaction(storeName, 'readwrite').objectStore(storeName).delete(key)
    request.onerror = () => reject(request.error)
    request.onsuccess = () => resolve()
  })

const clearStore = (database: IDBDatabase, storeName: string) =>
  new Promise<void>((resolve, reject) => {
    const request = database.transaction(storeName, 'readwrite').objectStore(storeName).clear()
    request.onerror = () => reject(request.error)
    request.onsuccess = () => resolve()
  })

const cacheKey = (tenantId: string, classId: string, subject: string, academicYear: string) =>
  `${tenantId}:${classId}:${subject.toLowerCase()}:${academicYear}`

/**
 * Caches only published curriculum releases and idempotent learner events.
 * Pinia can mirror this state for rendering, but never replaces IndexedDB as
 * the durable offline source.
 */
export function useCurriculumReleaseCache() {
  const getRelease = async (input: {
    tenantId: string
    classId: string
    subject: string
    academicYear: string
  }) => {
    if (!import.meta.client) return undefined
    const key = cacheKey(
      input.tenantId,
      input.classId,
      input.subject,
      input.academicYear,
    )
    const database = await openDatabase()
    try {
      if (navigator.onLine) {
        try {
          const result = await $fetch<{ data: CurriculumRelease }>(
            `/api/curriculum/classes/${input.classId}/current`,
            { params: input },
          )
          await write(database, releaseStore, result.data, key)
          return result.data
        } catch {
          // The last complete release remains usable when a connection is
          // captive, unstable, or the school server cannot be reached.
        }
      }
      return await read<CurriculumRelease>(database, releaseStore, key)
    } finally {
      database.close()
    }
  }

  const queueEvent = async (event: Omit<LearningEvent, 'attemptId'> & { attemptId?: string }) => {
    if (!import.meta.client) return
    const item: LearningEvent = {
      ...event,
      attemptId: event.attemptId ?? crypto.randomUUID(),
    }
    const database = await openDatabase()
    try {
      await write(database, eventStore, item)
    } finally {
      database.close()
    }
    await syncEvents()
    return item.attemptId
  }

  const syncEvents = () => {
    if (!import.meta.client || !navigator.onLine) return Promise.resolve()
    if (activeEventSync) return activeEventSync

    activeEventSync = (async () => {
      const database = await openDatabase()
      try {
        for (const event of await all<LearningEvent>(database, eventStore)) {
          try {
            await $fetch('/api/learning/events', { method: 'POST', body: event })
            await remove(database, eventStore, event.attemptId)
          } catch {
            // Keep the event for the next online opportunity. The server uses
            // attemptId as an idempotency key, so an ambiguous response is safe.
          }
        }
      } finally {
        database.close()
      }
    })().finally(() => {
      activeEventSync = null
    })

    return activeEventSync
  }

  const clear = async () => {
    if (!import.meta.client || !('indexedDB' in window)) return
    const database = await openDatabase()
    try {
      await Promise.all([clearStore(database, releaseStore), clearStore(database, eventStore)])
    } finally {
      database.close()
    }
  }

  return { getRelease, queueEvent, syncEvents, clear }
}
